import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTableWithControls from "../components/dataTable/DataTableWithControls";
import SectionHeader from "../components/section/SectionHeader";
import {
  ADD_ORGANIZATION_MAPPING_FORM_FIELDS,
  ORGANIZATION_MAPPING_FILTER_COLUMNS,
  ORGANIZATION_MAPPING_TABLE_HEADERS,
} from "../constants";
import AuthContext from "../store/authContext";
import {
  createSearchConfig,
  errorAlert,
  makeApiRequest,
  successAlert,
  transformMappingRows,
} from "../util";
import AdvancedSearch from "../components/advancedSearch/AdvancedSearch";
import { deleteOrganizationMapping } from "../api";
import DeleteModal from "../components/modal/DeleteModal";
import Button, { Icons } from "../components/buttons/Button";
import {
  DELETE_ORG_MAPPING_TRANSLATIONS,
  READ_ORG_MAPPING_TRANSLATIONS,
} from "../translations/util";
import useAppTitle from "../hooks/useAppTitle";
import useHideSidePanel from "../hooks/useHideSidePanel";
import BulkUpload from "../components/bulkUpload/BulkUpload";

interface ISearchData {
  "IWF-ID"?: string | null;
  "ORG-ID"?: string | null;
}

const INITIAL_STATE = {
  "IWF-ID": "",
  "ORG-ID": "",
};

const searchFields = ["IWF-ID", "ORG-ID"];

const searchConfigNew = createSearchConfig(
  searchFields,
  ADD_ORGANIZATION_MAPPING_FORM_FIELDS
);

const OrganisationMapping = () => {
  const {
    token,
    scopes: { org_mappings: orgMappings },
  } = useContext(AuthContext);
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const {
    showSidePanel,
    showAdvancedSearch,
    handleToggleAdvanceSearchPanel,
    handleToggleSidePanel,
  } = useHideSidePanel();
  const [selectedConnectionId, setSelectedConnectionId] = useState<
    string | null
  >(null);
  useAppTitle("Organisation Mappings- IWF Config Admin Tool (ICAT) by AT&T");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [locationData, setLocationsData] = useState<any[]>([]);
  const [searchData, setSearchData] = useState<ISearchData>(INITIAL_STATE);
  const [selectedColumn, setSelectedColumn] = useState<{
    id: string;
    value: string;
  } | null>(null);
  const [numberOfRows, setNumberOfRows] = useState(0);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [searchQuery, setSearhQuery] = useState<{
    data: ISearchData;
    pageNum: number;
    pageSize: number;
    sortByField?: string;
    sortDirection?: "desc" | "asc";
  }>({
    pageSize: 20,
    pageNum: 1,
    data: {},
    sortByField: undefined,
    sortDirection: undefined,
  });

  // State to check if Bulk Upload is Clicked
  const [isBulkUploadEnabled, setIsBulkUploadEnabled] = useState(false);

  const fetchMappings = async () => {
    setLoading(true);
    try {
      const { data, pageNum, pageSize, sortByField, sortDirection } =
        searchQuery;
      const queryParams = new URLSearchParams({
        page: `${pageNum}`,
        pageSize: `${pageSize}`,
        ...(sortByField && { sortByField }),
        ...(sortDirection && { sortDirection }),
        ...(data &&
          data["IWF-ID"] && {
            "IWF-ID": data["IWF-ID"],
          }),
        ...(data &&
          data["ORG-ID"] && {
            "ORG-ID": data["ORG-ID"],
          }),
      });
      const response = await makeApiRequest<{
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Rows: Array<any>;
        NbrOfRows: number;
        NbrOfPages: number;
      }>({
        path: `/api/v1/app-config/mappings?${queryParams}`,
        token,
      });
      setNumberOfRows(response.NbrOfRows);
      setNumberOfPages(response.NbrOfPages);
      setLocationsData(response.Rows);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMappings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  useEffect(() => {
    if (!showAdvancedSearch && Object.keys(searchQuery.data).length === 0) {
      setSearchData(INITIAL_STATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdvancedSearch]);

  const sortTable = (col: string, direction: "asc" | "desc") => () => {
    setSearhQuery({
      ...searchQuery,
      sortByField: col,
      sortDirection: direction,
    });
  };

  const deleteConnection = async (ref: string) => {
    if (token) {
      try {
        await deleteOrganizationMapping({
          token,
          ref,
        });
        setSelectedConnectionId(null);
        fetchMappings();
        successAlert(DELETE_ORG_MAPPING_TRANSLATIONS.successAlertMessage);
        setOpen(false);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        errorAlert(e);
      }
    }
  };

  return (
    <>
      <SectionHeader
        pageTitle={READ_ORG_MAPPING_TRANSLATIONS.header}
        pageDescription={READ_ORG_MAPPING_TRANSLATIONS.headerSubtext}
        sideComponent={<div className="dropdown-details" />}
      />

      <>
        {showAdvancedSearch ? (
          <AdvancedSearch
            setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
            advancedSearchState={searchData}
            advancedSearchConfig={searchConfigNew}
            setAdvancedSearchState={setSearchData}
            onSubmit={(data) =>
              setSearhQuery({ ...searchQuery, pageNum: 1, pageSize: 20, data })
            }
            onCancel={() => {
              setSearchData(INITIAL_STATE);
              handleToggleAdvanceSearchPanel();
            }}
          />
        ) : null}

        {showSidePanel && isBulkUploadEnabled ? (
          <BulkUpload
            moduleName="Organisation Mapping"
            pathName="/api/v1/app-config/mappings"
            csvTemplateFile={["IWF-ID,ORGANISATION-ID"]}
            closeBulkUploadSidePanel={() => {
              handleToggleSidePanel();
              setIsBulkUploadEnabled(false);
              fetchMappings();
            }}
          />
        ) : null}

        <DeleteModal
          modalHeaderText={READ_ORG_MAPPING_TRANSLATIONS.deleteModal.header}
          modalDescriptionText={READ_ORG_MAPPING_TRANSLATIONS.deleteModal.description(
            selectedConnectionId
          )}
          onDelete={() =>
            selectedConnectionId && deleteConnection(selectedConnectionId)
          }
          open={open}
          setOpen={setOpen}
        />

        <DataTableWithControls
          loading={loading}
          filterColumns={ORGANIZATION_MAPPING_FILTER_COLUMNS}
          selectedColumn={selectedColumn}
          setSelectedColumn={setSelectedColumn}
          onSearch={(term) =>
            setSearhQuery({
              ...searchQuery,
              pageNum: 1,
              pageSize: 20,
              data: { [`${selectedColumn?.id}`]: term },
            })
          }
          setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
          showCreateButton={orgMappings && orgMappings.includes("c")}
          onCreateButtonClick={() => {
            navigate(`/configurationManagement/organizationmapping/new`);
          }}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          rows={transformMappingRows(locationData).map((row: any) => ({
            ...row,
            action: (
              <Button
                kind="ghost"
                className="bg-btn"
                icon={Icons.Delete}
                disabled={!(orgMappings && orgMappings.includes("d"))}
                onClick={() => {
                  setSelectedConnectionId(row.id);
                  setOpen(true);
                }}
              />
            ),
          }))}
          numberOfAdvancedSearchFilters={searchConfigNew.reduce(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (acc, el: any) => {
              const { name } = el;
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const obj: any = { ...searchData };
              return acc + (obj[name] && obj[name].length > 0 ? 1 : 0);
            },
            0
          )}
          clearAdvancedSearchFilters={() => {
            setSearchData(INITIAL_STATE);
            setSearhQuery({
              ...searchQuery,
              pageNum: 1,
              pageSize: 20,
              data: {},
            });
          }}
          headers={ORGANIZATION_MAPPING_TABLE_HEADERS}
          pageNum={searchQuery.pageNum}
          setPageNum={(pageNum) => setSearhQuery({ ...searchQuery, pageNum })}
          totalItems={numberOfRows}
          totalPages={numberOfPages}
          pageSize={searchQuery.pageSize}
          setPageSize={(pageSize) =>
            setSearhQuery({ ...searchQuery, pageSize })
          }
          sortByField={searchQuery.sortByField}
          sortDirection={searchQuery.sortDirection}
          handleSort={sortTable}
          showBulkUploadButton={orgMappings && orgMappings.includes("c")}
          onBulkUploadButtonClick={() => {
            handleToggleSidePanel();
            setIsBulkUploadEnabled(true);
          }}
        />
      </>
    </>
  );
};

export default OrganisationMapping;
